import { lazy, Suspense } from 'react';
import { useAccess } from '../../../parts/access.js';
import { AdminFrame } from './AdminFrame.js';
import { AdminHome } from '../home/AdminHome.js';
import { PeoplePage } from '../people/PeoplePage.js';
import { GroupPage } from '../group/GroupPage.js';
import { PersonPage } from '../person/PersonPage.js';
import { EveryChange } from '../changes/EveryChange.js';
import { AddPerson } from '../add/AddPerson.js';
import { SourcesArea } from '../sources/SourcesArea.js';
import { PrivacyArea } from '../privacy/PrivacyArea.js';

// Brief 5: the keeper's areas are one chunk, loaded only for access.person.keeper, so nobody else's browser receives
// their words, their routes or the vault's name (R22).
const KeeperArea = lazy(() => import('../keeper/KeeperArea.js'));
const KEEPER_AREAS = ['spend', 'decides', 'policy', 'vault', 'powers'] as const;
const AREA_PAGES = ['sources', 'privacy', ...KEEPER_AREAS] as const;
type KeeperAreaName = typeof KEEPER_AREAS[number];
export function isKeeperArea(area: string | undefined): area is KeeperAreaName {
  return (KEEPER_AREAS as readonly string[]).includes(area ?? '');
}
export function isAreaPage(area: string | undefined): area is typeof AREA_PAGES[number] {
  return (AREA_PAGES as readonly string[]).includes(area ?? '');
}

/** Which Admin page an /admin address draws. App.tsx's surfaceOf fills it; each area's item adds its own case here
 * (MA4: people, group, person, changes, add; MA5: sources, privacy and the keeper's areas, agreed with track C). */
export interface AdminSurface { area?: 'people' | 'group' | 'person' | 'changes' | 'add' | typeof AREA_PAGES[number]; id?: string; person?: string; group?: string }

export function AdminRouter({ surface }: { surface: AdminSurface }) {
  const keeper = useAccess()?.person?.keeper === true;
  let page;
  switch (surface.area) {
    case 'people': page = <PeoplePage />; break;
    case 'group': page = <GroupPage key={surface.id} groupId={surface.id!} />; break;
    case 'person': page = <PersonPage key={surface.id} principalId={surface.id!} />; break;
    case 'changes': page = <EveryChange principalId={surface.person} groupId={surface.group} />; break;
    case 'add': page = <AddPerson />; break;
    case 'sources': page = <SourcesArea />; break;
    case 'privacy': page = <PrivacyArea />; break;
    case 'spend': case 'decides': case 'policy': case 'vault': case 'powers':
      page = keeper ? <Suspense fallback={null}><KeeperArea area={surface.area} /></Suspense> : null; break;
    default: page = <AdminHome />;
  }
  return <AdminFrame>{page}</AdminFrame>;
}
